import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { hueOf, initialsOf, photoOf, STATUS_META } from "./presence";

/**
 * Discord member avatar: initials tinted by a stable per-name hue, with an
 * optional status dot (green working / amber idle / faint checked out).
 */
const PREVIEW_PX = 280;

const PresenceAvatar = ({ name, userId, size = 42, status, className = "", title }) => {
  const photo = userId ? photoOf(userId) : null;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [photo]);
  const rootRef = React.useRef(null);
  const [rect, setRect] = React.useState(null);
  const showPhoto = Boolean(photo) && !failed;
  React.useEffect(() => {
    if (!rect) return undefined;
    const close = () => setRect(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [rect]);
  // Slide out to the right of the avatar; flip left / clamp vertically if no room.
  let pos = null;
  if (rect) {
    const gap = 10;
    const fitsRight = rect.right + gap + PREVIEW_PX <= window.innerWidth - 8;
    pos = {
      left: fitsRight ? rect.right + gap : Math.max(8, rect.left - gap - PREVIEW_PX),
      top: Math.min(Math.max(8, rect.top), window.innerHeight - PREVIEW_PX - 8),
      fromX: fitsRight ? -24 : 24,
      originX: fitsRight ? "left" : "right",
    };
  }
  const h = hueOf(name);
  const dot = status ? STATUS_META[status]?.dot : null;
  const dotSize = size >= 40 ? 14 : size >= 30 ? 11 : 9;
  return (
    <span className={`relative inline-flex shrink-0 ${className}`} title={title}>
      <span
        ref={rootRef}
        onClick={
          showPhoto
            ? (e) => {
                e.stopPropagation();
                setRect(rootRef.current.getBoundingClientRect());
              }
            : undefined
        }
        className={`rounded-full overflow-hidden flex items-center justify-center font-semibold select-none ${showPhoto ? "cursor-zoom-in" : ""}`}
        style={{
          width: size,
          height: size,
          fontSize: Math.max(9, Math.round(size * 0.34)),
          color: `hsl(${h} 70% 62%)`,
          background: `hsl(${h} 70% 55% / 0.16)`,
        }}
      >
        {showPhoto ? (
          <img src={photo} alt={name} className="w-full h-full rounded-full object-cover" onError={() => setFailed(true)} />
        ) : (
          initialsOf(name)
        )}
      </span>
      {dot && (
        <span
          className="absolute -bottom-0.5 -right-0.5 rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            background: dot,
            border: `${size >= 40 ? 3 : 2}px solid var(--panel-solid)`,
            boxShadow: status === "out" ? "none" : `0 0 8px ${dot}`,
          }}
        />
      )}
      {rect &&
        createPortal(
          <AnimatePresence>
            <div
              key="backdrop"
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                e.stopPropagation();
                setRect(null);
              }}
            />
            <motion.img
              key="preview"
              src={photo}
              alt={name}
              initial={{ opacity: 0, x: pos.fromX, scale: 0.6 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
              style={{ left: pos.left, top: pos.top, width: PREVIEW_PX, height: PREVIEW_PX, transformOrigin: `${pos.originX} top` }}
              className="fixed z-[9999] rounded-2xl object-cover object-top shadow-2xl border border-line2 bg-panel2"
            />
          </AnimatePresence>,
          document.body,
        )}
    </span>
  );
};

export default PresenceAvatar;
